import { useCallback, useEffect, useMemo, useState } from 'react';


/**
 *
 * This hook returns data url of the refined image based on the following parameter object
 * and since this is asynchronouse returns object with isLoading, refinementDataUrl,
 * and error attributes.
 * @param Object with following properties {
 * src - image URL of the original asset to be refined,
 * left - x coordinate of the crop area against original or zoomed image, when zoom applied,
 * top - y coordinate of the crop area against original or zoomed image, when zoom applied,
 * frameWidth - width of the crop area against original or zoomed image, when zoom applied,
 * frameHeight - height of the crop area against original or zoomed image, when zoom applied,
 * canvasWidth - width of the canvas of original image or zoomed image whn zoom applied,
 * canvasHeight - height of the canvas of original image or zoomed image whn zoom applied,
 * opacity - value from 0 to 1 defining opacity of the image,
 * skip - boolean value to skip the refinement process when  conditional calls needed,
 * to avoid conditional use of the hook
 * }
 * @returns Object with isLoading, refinementDataUrl, and error attributes.
 */
export default function useRefinementDataUrl({
	src,
	left = 0,
	top = 0,
	frameWidth,
	frameHeight,
	canvasWidth,
	canvasHeight,
	opacity = 1,
	skip = false,
}) {
	const TWO = 2
		, [refinementState, setRefinementState] = useState({ isLoading: false, refinementDataUrl: null })
		, [isImgLoading, setIsImgLoading] = useState(false)
		, [error, setError] = useState(null)
		, canvas = useMemo(() => document.createElement('canvas'), [])
		, img = useMemo(() => {
			const img = new Image();
			img.crossOrigin = "use-credentials";
			return img;
		}, [])
		, ctx = useMemo(() => canvas.getContext('2d'), []);

	// Skip the refinement process if skip is true
	// needed to avoid problem of conditional calling of hoks
	const drawCanvas = useCallback(() => {
		if (skip || !img.width || !canvasWidth) {
			return;
		}
		const { width } = img
			, zoom = canvasWidth / width
			, focalX = (left + frameWidth / TWO) / zoom
			, focalY = (top + frameHeight / TWO) / zoom;

		canvas.width = frameWidth;
		canvas.height = frameHeight;

		ctx.clearRect(0, 0, canvasWidth, canvasHeight);
		ctx.globalAlpha = opacity;
		ctx.drawImage(
			img,
			focalX - frameWidth / TWO / zoom,
			focalY - frameHeight / TWO / zoom,
			frameWidth / zoom,
			frameHeight / zoom,
			0,
			0,
			frameWidth,
			frameHeight
		);
		setRefinementState({ isLoading: false, refinementDataUrl: canvas.toDataURL() });
	}, [left, top, frameWidth, frameHeight, canvasWidth, canvasHeight, opacity, canvas, img.width, skip]);

	useEffect(() => {
		if (skip) {
			return;
		}

		const handleLoad = () => {
			setIsImgLoading(false);
			drawCanvas();
		};

		const handleError = (error) => {
			setError(error);
			setIsImgLoading(false);
			setRefinementState({ isLoading: false, refinementDataUrl: null });
		};

		// This is optimized if img.src does not change there will be no need to wait for the onload.
		if (!isImgLoading && img.complete) {
			setIsImgLoading(true);
			handleLoad();
		}

		img.onload = handleLoad;
		img.onerror = handleError;
	}, [img, drawCanvas, isImgLoading, skip]);

	//Only rerender image if src changes
	useEffect(() => {
		if (skip) {
			return;
		}

		setIsImgLoading(true);
		img.src = src;
	}, [src, img, skip]);

	return { ...refinementState, error };
}
